import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PillarBadge from "@/components/PillarBadge";
import { PILLAR_LABEL_BY_SLUG } from "@/lib/pillars";
import { MapPin, Star, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import ImageCarousel from "@/components/ImageCarousel";
import AboutHost from "@/components/AboutHost";
import BookingBuilder from "@/components/BookingBuilder";
import MatchBadge from "@/components/MatchBadge";

export default function ExperienceDetail() {
  const { id } = useParams();
  const [exp, setExp] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/experiences/${id}`).then((r) => setExp(r.data)).catch(() => setExp(false));
  }, [id]);

  const avgRating = useMemo(
    () => (exp && exp.reviews?.length ? (exp.reviews.reduce((s, r) => s + r.rating, 0) / exp.reviews.length).toFixed(1) : null),
    [exp]
  );
  const locationLine = useMemo(
    () => (exp ? [exp.location, exp.state, exp.country].filter(Boolean).join(", ") : ""),
    [exp]
  );
  const depthEntries = useMemo(
    () => (exp?.transformation?.depth ? Object.entries(exp.transformation.depth).filter(([, v]) => v > 0) : []),
    [exp]
  );

  if (exp === false) return (
    <div className="min-h-screen bg-canvas"><Navbar /><div className="p-24 text-center">Experience not found.</div><Footer /></div>
  );
  if (!exp) return <div className="min-h-screen bg-canvas"><Navbar /><div className="p-24 text-center text-muted2">Loading…</div></div>;

  const messageHost = async () => {
    if (!user) { navigate("/login?redirect=/experience/" + id); return; }
    try {
      const { data } = await api.post("/messages/thread", { experience_id: id });
      navigate(`/messages/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open thread");
    }
  };

  return (
    <div className="min-h-screen bg-canvas">
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-10">
        <div className="flex flex-wrap gap-1.5 mb-3 items-center">
          {exp.pillars?.map((p) => <PillarBadge key={p} pillar={p} />)}
          {typeof exp.match_score === "number" && <MatchBadge score={exp.match_score} size="lg" />}
        </div>
        <h1 className="font-serif text-4xl md:text-6xl tracking-tight leading-none">{exp.title}</h1>
        <div className="mt-2 overline text-moss no-justify" data-testid="property-tagline">Live, Work, Explore and Share.</div>
        <p className="mt-3 text-lg text-muted2 max-w-2xl italic">{exp.tagline}</p>
        <div className="flex flex-wrap gap-6 mt-5 text-sm text-muted2">
          <span className="inline-flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            {locationLine}
            {exp.pin_code ? ` — ${exp.pin_code}` : ""}
          </span>
          {avgRating && <span className="inline-flex items-center gap-2"><Star className="h-4 w-4 fill-terracotta text-terracotta" />{avgRating} · {exp.reviews.length} reviews</span>}
        </div>
        {exp.address && (
          <p className="mt-2 text-xs text-muted2 italic">Exact location shared after booking · {exp.address.split(",")[0]}…</p>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 mt-8">
        <ImageCarousel images={exp.images || []} />
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 mt-14 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-7 space-y-10">
          <div>
            <div className="overline mb-3">About this journey</div>
            <p className="text-base leading-relaxed text-ink whitespace-pre-line">{exp.description}</p>
          </div>

          <div>
            <div className="overline mb-3">Activities</div>
            <ul className="grid sm:grid-cols-2 gap-y-2 gap-x-6">
              {exp.activities?.map((a) => (
                <li key={a} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-moss mt-0.5 shrink-0" /> {a}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="overline mb-3">What's included</div>
            <ul className="grid sm:grid-cols-2 gap-y-2 gap-x-6">
              {exp.included?.map((a) => (
                <li key={a} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-terracotta mt-0.5 shrink-0" /> {a}
                </li>
              ))}
            </ul>
          </div>

          {exp.transformation && (exp.transformation.philosophy || exp.transformation.promise || exp.transformation.not_for || (exp.transformation.focus_tags || []).length > 0 || Object.keys(exp.transformation.depth || {}).length > 0) && (
            <div data-testid="host-story-section">
              <div className="overline mb-3">The host's story</div>
              {exp.transformation.philosophy && (
                <p className="font-serif italic text-lg leading-relaxed text-ink mb-4">"{exp.transformation.philosophy}"</p>
              )}
              {exp.transformation.promise && (
                <div className="mb-4">
                  <div className="text-xs uppercase tracking-widest text-moss mb-1">What will change in you</div>
                  <p className="text-base leading-relaxed">{exp.transformation.promise}</p>
                </div>
              )}
              {exp.transformation.not_for && (
                <div className="mb-4">
                  <div className="text-xs uppercase tracking-widest text-terracotta mb-1">Honest note — this is not for</div>
                  <p className="text-sm text-muted2">{exp.transformation.not_for}</p>
                </div>
              )}
              {depthEntries.length > 0 && (
                <div className="mb-4">
                  <div className="text-xs uppercase tracking-widest text-muted2 mb-2">Depth on each pillar</div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {depthEntries.map(([p, v]) => (
                      <div key={p} className="flex items-center gap-3 text-sm">
                        <span className="w-24">{PILLAR_LABEL_BY_SLUG[p] || p}</span>
                        <div className="flex gap-1">
                          {[1,2,3,4,5].map(n => (
                            <span key={n} className={`w-3 h-3 rounded-full ${n <= v ? "bg-moss" : "bg-subtle"}`} />
                          ))}
                        </div>
                        <span className="text-xs text-muted2">{v}/5</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(exp.transformation.focus_tags || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {exp.transformation.focus_tags.map(t => (
                    <span key={t} className="text-xs bg-terracotta/10 text-terracotta rounded-full px-2.5 py-1">#{t}</span>
                  ))}
                </div>
              )}
            </div>
          )}

          {exp.wellness && (
            (exp.wellness.yoga_styles?.length ||
             exp.wellness.meditation_types?.length ||
             exp.wellness.dietary?.length ||
             exp.wellness.practitioners?.length ||
             exp.wellness.medical_notes)
          ) && (
            <div data-testid="wellness-section">
              <div className="overline mb-3">Wellness on the property</div>
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                {exp.wellness.yoga_styles?.length > 0 && (
                  <div><span className="text-muted2 text-xs uppercase tracking-widest block mb-1">Yoga styles</span>{exp.wellness.yoga_styles.join(" · ")}</div>
                )}
                {exp.wellness.meditation_types?.length > 0 && (
                  <div><span className="text-muted2 text-xs uppercase tracking-widest block mb-1">Meditation</span>{exp.wellness.meditation_types.join(" · ")}</div>
                )}
                {exp.wellness.dietary?.length > 0 && (
                  <div><span className="text-muted2 text-xs uppercase tracking-widest block mb-1">Dietary</span>{exp.wellness.dietary.join(" · ")}</div>
                )}
                {exp.wellness.practitioners?.length > 0 && (
                  <div><span className="text-muted2 text-xs uppercase tracking-widest block mb-1">On-site practitioners</span>
                    {exp.wellness.practitioners.map(p => <div key={p.name}>{p.name}{p.credentials ? ` · ${p.credentials}` : ""}</div>)}
                  </div>
                )}
              </div>
              {exp.wellness.medical_notes && (
                <p className="text-xs text-muted2 italic mt-4">{exp.wellness.medical_notes}</p>
              )}
            </div>
          )}

          {exp.facilities?.length > 0 && (
            <div data-testid="facilities-section">
              <div className="overline mb-3">Facilities & amenities</div>
              <div className="flex flex-wrap gap-1.5">
                {exp.facilities.map((f) => (
                  <span key={f} className="text-xs bg-subtle text-ink rounded-full px-3 py-1.5 border border-subtle">{f}</span>
                ))}
              </div>
            </div>
          )}

          {exp.place_intro && (
            <div data-testid="place-intro-section">
              <div className="overline mb-3">About the place & community</div>
              <p className="text-base leading-relaxed text-ink whitespace-pre-line">{exp.place_intro}</p>
            </div>
          )}

          {exp.community_connect?.length > 0 && (
            <div data-testid="community-connect-section">
              <div className="overline mb-3">Connect with the community</div>
              <div className="grid sm:grid-cols-2 gap-4">
                {exp.community_connect.map((c) => (
                  <div key={c.id || c.title} data-testid={`cc-card-${c.id || c.title}`} className="bg-white border border-subtle rounded-2xl p-5">
                    <div className="font-serif text-lg leading-tight">{c.title}</div>
                    {c.description && <p className="text-sm text-muted2 mt-1.5 whitespace-pre-line">{c.description}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="overline mb-3">About the host</div>
            <AboutHost hostId={exp.host_id} fallbackName={exp.host_name} fallbackLocation={`${exp.location}, ${exp.country}`} />
          </div>

          {exp.reviews?.length > 0 && (
            <div>
              <div className="overline mb-4">Words from travelers</div>
              <div className="space-y-6">
                {exp.reviews.map((r) => (
                  <div key={r.id} className="border-l-2 border-moss pl-5">
                    <div className="flex items-center gap-2 text-sm mb-1">
                      <span className="font-medium">{r.traveler_name}</span>
                      <span className="text-terracotta">{"★".repeat(r.rating)}</span>
                    </div>
                    <p className="text-sm text-muted2 italic">"{r.comment}"</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="md:col-span-5">
          <BookingBuilder exp={exp} onMessageHost={messageHost} />
        </div>
      </section>

      <Footer />
    </div>
  );
}
